import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router";
import { CrudPage } from "@/components/crud/CrudPage";
import { DataTable } from "@/components/DataTable";
import { FilterModal } from "@/components/FilterModal";
import { getModules, getUsersByModule, type Module, type ModuleUser } from "@/api/module.service";
import { getModulesByClient } from "@/api/client.service";
import type { Company } from "@/api/company.service";
import {
  createUserCompanyModule,
  deleteUserCompanyModule,
  getUserClients,
  getUserCompanies,
  getUsers,
  UserRoles,
  type User,
} from "@/api/user.service";
import { useAuth } from "@/context/AuthContext";
import { useNotification } from "@/context/NotificationContext";
import usePagination from "@/hooks/pagination";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Add, Delete } from "@mui/icons-material";

interface ModuleUserCompany {
  company_id: string;
  company_name: string;
  client_id: string;
}

interface GroupedModuleUser {
  user_id: string;
  user_name: string;
  user_email: string;
  companies: ModuleUserCompany[];
}

interface RemoveTarget {
  user: GroupedModuleUser;
  company?: ModuleUserCompany;
}

export function ModuleUsersPage() {
  const { moduleId } = useParams<{ moduleId: string }>();
  const { user } = useAuth();
  const { notify } = useNotification();
  const isAdmin = user?.role === UserRoles.Admin;

  const [module, setModule] = useState<Module | null>(null);
  const [moduleUsers, setModuleUsers] = useState<ModuleUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [removing, setRemoving] = useState(false);
  const [confirmRemoveTarget, setConfirmRemoveTarget] = useState<RemoveTarget | null>(null);
  const [companiesDialogUserId, setCompaniesDialogUserId] = useState<string | null>(null);

  const { page, setPage, rowsPerPage, setRowsPerPage } = usePagination(0, 10);
  const [inputValues, setInputValues] = useState({ name: "", email: "", company: "" });
  const [filters, setFilters] = useState({ name: "", email: "", company: "" });

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [userOptions, setUserOptions] = useState<User[]>([]);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [companyOptions, setCompanyOptions] = useState<Company[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [granting, setGranting] = useState(false);
  const searchDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [scopedClientId, setScopedClientId] = useState<string | undefined>(undefined);
  const [ownCompanyIds, setOwnCompanyIds] = useState<Set<string> | null>(null);

  useEffect(() => {
    if (!user || isAdmin) return;

    getUserCompanies(user.id)
      .then((companies) => setOwnCompanyIds(new Set(companies.map((c) => c.id))))
      .catch((error) => {
        console.error("Erro ao carregar empresas do usuário logado:", error);
        setOwnCompanyIds(new Set());
      });
  }, [user, isAdmin]);

  const loadModuleUsers = async (clientId: string | undefined) => {
    if (!moduleId) return;
    setLoadingUsers(true);
    try {
      const data = await getUsersByModule(moduleId, clientId);
      setModuleUsers(data ?? []);
    } catch (error) {
      console.error("Erro ao carregar usuários do módulo:", error);
      notify("Erro ao carregar usuários do módulo", "error");
      setModuleUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    if (!user || !moduleId) return;

    const loadModuleAndUsers = async () => {
      try {
        const allModules = await getModules();

        if (isAdmin) {
          setModule(allModules.find((m) => m.id === moduleId) ?? null);
          await loadModuleUsers(undefined);
          return;
        }

        let clientId = user.loggedClient?.id;
        if (!clientId) {
          const clients = await getUserClients(user.id);
          clientId = clients.length === 1 ? clients[0]?.id : undefined;
        }
        setScopedClientId(clientId);

        if (clientId) {
          const clientModules = await getModulesByClient(clientId);
          const allowedIds = new Set(clientModules.map((m) => m.module_id));
          setModule(allowedIds.has(moduleId) ? allModules.find((m) => m.id === moduleId) ?? null : null);
        }

        await loadModuleUsers(clientId);
      } catch (error) {
        console.error("Erro ao carregar módulo:", error);
        notify("Erro ao carregar módulo", "error");
      }
    };

    loadModuleAndUsers();
  }, [user, isAdmin, moduleId]);

  const refreshModuleUsers = async () => {
    await loadModuleUsers(!isAdmin ? scopedClientId : undefined);
  };

  const groupedUsers = useMemo(() => {
    const byUser = new Map<string, GroupedModuleUser>();

    for (const mu of moduleUsers) {
      let grouped = byUser.get(mu.user_id);
      if (!grouped) {
        grouped = {
          user_id: mu.user_id,
          user_name: mu.user_name,
          user_email: mu.user_email,
          companies: [],
        };
        byUser.set(mu.user_id, grouped);
      }
      if (!grouped.companies.some((c) => c.company_id === mu.company_id)) {
        grouped.companies.push({
          company_id: mu.company_id,
          company_name: mu.company_name,
          client_id: mu.client_id,
        });
      }
    }

    const list = Array.from(byUser.values());
    for (const grouped of list) {
      grouped.companies.sort((a, b) => a.company_name.localeCompare(b.company_name));
    }
    return list.sort((a, b) => a.user_name.localeCompare(b.user_name));
  }, [moduleUsers]);

  const handleConfirmRemoveAccess = async () => {
    const target = confirmRemoveTarget;
    if (!moduleId || !target) return;

    const companies = target.company ? [target.company] : target.user.companies;

    setRemoving(true);
    try {
      for (const company of companies) {
        await deleteUserCompanyModule({
          user_id: target.user.user_id,
          company_id: company.company_id,
          module_id: moduleId,
        });
      }

      const removedIds = new Set(companies.map((c) => c.company_id));
      setModuleUsers((prev) =>
        prev.filter((mu) => !(mu.user_id === target.user.user_id && removedIds.has(mu.company_id)))
      );
      notify(
        companies.length > 1 ? "Acessos removidos com sucesso" : "Acesso removido com sucesso",
        "success"
      );
      setConfirmRemoveTarget(null);
    } catch (error) {
      console.error("Erro ao remover acesso ao módulo:", error);
      notify("Erro ao remover acesso ao módulo", "error");
    } finally {
      setRemoving(false);
    }
  };

  const handleFilterInputChange = (field: keyof typeof inputValues, value: string) => {
    setInputValues((prev) => ({ ...prev, [field]: value }));
  };

  const applyFilters = () => {
    setFilters({ ...inputValues });
    setPage(0);
  };

  const handleClearFilters = () => {
    const empty = { name: "", email: "", company: "" };
    setInputValues(empty);
    setFilters(empty);
    setPage(0);
  };

  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  const filteredUsers = useMemo(() => {
    const name = filters.name.trim().toLowerCase();
    const email = filters.email.trim().toLowerCase();
    const company = filters.company.trim().toLowerCase();

    return groupedUsers.filter((gu) => {
      if (name && !gu.user_name.toLowerCase().includes(name)) return false;
      if (email && !gu.user_email.toLowerCase().includes(email)) return false;
      if (company && !gu.companies.some((c) => c.company_name.toLowerCase().includes(company))) return false;
      return true;
    });
  }, [groupedUsers, filters]);

  const paginatedUsers = useMemo(
    () => filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredUsers, page, rowsPerPage]
  );

  const companiesDialogUser = useMemo(
    () => groupedUsers.find((gu) => gu.user_id === companiesDialogUserId) ?? null,
    [groupedUsers, companiesDialogUserId]
  );

  useEffect(() => {
    if (companiesDialogUserId && !companiesDialogUser) setCompaniesDialogUserId(null);
  }, [companiesDialogUserId, companiesDialogUser]);

  const handleOpenAddDialog = () => {
    setSelectedUser(null);
    setCompanyOptions([]);
    setSelectedCompany(null);
    setUserOptions([]);
    setAddDialogOpen(true);
  };

  const handleCloseAddDialog = () => {
    setAddDialogOpen(false);
    setSelectedUser(null);
    setCompanyOptions([]);
    setSelectedCompany(null);
  };

  const handleUserSearch = (query: string) => {
    if (searchDebounce.current) clearTimeout(searchDebounce.current);

    if (!query.trim()) {
      setUserOptions([]);
      return;
    }

    searchDebounce.current = setTimeout(async () => {
      setSearchingUsers(true);
      try {
        const result = await getUsers({ offset: 0, limit: 20, name: query });
        setUserOptions(result.users);
      } catch (error) {
        console.error("Erro ao buscar usuários:", error);
        notify("Erro ao buscar usuários", "error");
      } finally {
        setSearchingUsers(false);
      }
    }, 400);
  };

  const handleSelectUser = async (nextUser: User | null) => {
    setSelectedUser(nextUser);
    setSelectedCompany(null);
    setCompanyOptions([]);
    if (!nextUser) return;

    setLoadingCompanies(true);
    try {
      const companies = await getUserCompanies(nextUser.id);
      const scopedCompanies = isAdmin ? companies : companies.filter((c) => ownCompanyIds?.has(c.id));
      setCompanyOptions(scopedCompanies);
    } catch (error) {
      console.error("Erro ao buscar empresas do usuário:", error);
      notify("Erro ao buscar empresas do usuário", "error");
    } finally {
      setLoadingCompanies(false);
    }
  };

  const handleGrantAccess = async () => {
    if (!moduleId || !selectedUser || !selectedCompany) return;

    setGranting(true);
    try {
      await createUserCompanyModule({
        user_id: selectedUser.id,
        company_id: selectedCompany.id,
        module_id: moduleId,
      });
      notify("Usuário adicionado ao módulo com sucesso", "success");
      handleCloseAddDialog();
      await refreshModuleUsers();
    } catch (error) {
      console.error("Erro ao adicionar usuário ao módulo:", error);
      notify("Erro ao adicionar usuário ao módulo", "error");
    } finally {
      setGranting(false);
    }
  };

  return (
    <CrudPage
      title={`Usuários com acesso a "${module?.label ?? "..."}"`}
      subtitle="Gerencie os usuários e empresas com acesso a este módulo"
      headerActions={
        <Button variant="contained" startIcon={<Add />} onClick={handleOpenAddDialog}>
          Adicionar usuário
        </Button>
      }
    >
      <FilterModal activeCount={activeFilterCount} onApply={applyFilters} onClear={handleClearFilters}>
        <TextField
          label="Usuário"
          size="small"
          fullWidth
          value={inputValues.name}
          onChange={(e) => handleFilterInputChange("name", e.target.value)}
        />
        <TextField
          label="Email"
          size="small"
          fullWidth
          value={inputValues.email}
          onChange={(e) => handleFilterInputChange("email", e.target.value)}
        />
        <TextField
          label="Empresa"
          size="small"
          fullWidth
          value={inputValues.company}
          onChange={(e) => handleFilterInputChange("company", e.target.value)}
        />
      </FilterModal>

      {loadingUsers ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : filteredUsers.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
          {groupedUsers.length === 0
            ? "Nenhum usuário tem acesso a este módulo."
            : "Nenhum usuário encontrado para os filtros aplicados."}
        </Typography>
      ) : (
        <DataTable
          data={paginatedUsers}
          columns={[
            { key: "user_name", label: "Usuário" },
            { key: "user_email", label: "Email" },
            {
              key: "companies",
              label: "Empresas",
              render: (gu) =>
                gu.companies.length === 1 ? (
                  <Typography variant="body2" component="span">
                    {gu.companies[0]?.company_name}
                  </Typography>
                ) : (
                  <Typography
                    variant="body2"
                    component="span"
                    onClick={(e) => {
                      e.stopPropagation();
                      setCompaniesDialogUserId(gu.user_id);
                    }}
                    sx={{ cursor: "pointer", textDecoration: "underline" }}
                  >
                    {gu.companies.length} empresas
                  </Typography>
                ),
            },
          ]}
          total={filteredUsers.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={setPage}
          onRowsPerPageChange={setRowsPerPage}
          actions={(gu) => (
            <IconButton
              size="small"
              color="error"
              title={
                gu.companies.length > 1
                  ? `Remover acesso em todas as ${gu.companies.length} empresas`
                  : "Remover acesso"
              }
              onClick={() => setConfirmRemoveTarget({ user: gu })}
            >
              <Delete fontSize="small" />
            </IconButton>
          )}
        />
      )}

      <Dialog
        open={!!companiesDialogUser}
        onClose={() => setCompaniesDialogUserId(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Empresas de {companiesDialogUser?.user_name}
          <Typography variant="body2" color="text.secondary">
            Acessos ao módulo "{module?.label}"
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <List dense disablePadding>
            {companiesDialogUser?.companies.map((company, index) => (
              <Box key={company.company_id}>
                {index > 0 && <Divider component="li" />}
                <ListItem
                  secondaryAction={
                    <IconButton
                      edge="end"
                      size="small"
                      color="error"
                      title="Remover acesso nesta empresa"
                      onClick={() =>
                        setConfirmRemoveTarget({ user: companiesDialogUser, company })
                      }
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  }
                >
                  <ListItemText primary={company.company_name} />
                </ListItem>
              </Box>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompaniesDialogUserId(null)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!confirmRemoveTarget} onClose={() => setConfirmRemoveTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Remover acesso</DialogTitle>
        <DialogContent dividers>
          {confirmRemoveTarget && (
            <Typography variant="body2">
              {confirmRemoveTarget.company || confirmRemoveTarget.user.companies.length === 1 ? (
                <>
                  Tem certeza que deseja remover o acesso de{" "}
                  <strong>{confirmRemoveTarget.user.user_name}</strong> ao módulo "{module?.label}" na empresa{" "}
                  <strong>
                    {(confirmRemoveTarget.company ?? confirmRemoveTarget.user.companies[0])?.company_name}
                  </strong>
                  ?
                </>
              ) : (
                <>
                  Tem certeza que deseja remover o acesso de{" "}
                  <strong>{confirmRemoveTarget.user.user_name}</strong> ao módulo "{module?.label}" em todas as{" "}
                  <strong>{confirmRemoveTarget.user.companies.length} empresas</strong>?
                </>
              )}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRemoveTarget(null)} disabled={removing}>
            Cancelar
          </Button>
          <Button variant="contained" color="error" onClick={handleConfirmRemoveAccess} disabled={removing}>
            Remover
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={addDialogOpen} onClose={handleCloseAddDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Adicionar usuário a "{module?.label}"</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Autocomplete
              options={userOptions}
              value={selectedUser}
              loading={searchingUsers}
              filterOptions={(x) => x}
              onChange={(_, newValue) => handleSelectUser(newValue)}
              onInputChange={(_, newInputValue) => handleUserSearch(newInputValue)}
              getOptionLabel={(option) => `${option.name} (${option.email})`}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField {...params} label="Buscar usuário" placeholder="Digite o nome do usuário" />
              )}
              noOptionsText="Digite para buscar"
            />

            <Autocomplete
              options={companyOptions}
              value={selectedCompany}
              loading={loadingCompanies}
              disabled={!selectedUser}
              onChange={(_, newValue) => setSelectedCompany(newValue)}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField {...params} label="Empresa" placeholder="Selecione a empresa" />
              )}
              noOptionsText="Nenhuma empresa vinculada a este usuário"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddDialog} disabled={granting}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            disabled={!selectedUser || !selectedCompany || granting}
            onClick={handleGrantAccess}
          >
            Adicionar
          </Button>
        </DialogActions>
      </Dialog>
    </CrudPage>
  );
}
